import * as yup from 'yup';
import { RendererType } from '../data-models/field-definition';
import { percentField0to100, plainDateField, usdField } from './common-schema-defs';

// Mirrors the api's fund.allocationSettings JSON; the values must match the api's.
export const AllocationBasis = {
  committed: 'committed',
  netInvestable: 'net-investable',
} as const;
export type AllocationBasis = (typeof AllocationBasis)[keyof typeof AllocationBasis];

export const FeeBasis = {
  committed: 'committed',
  investedCost: 'invested-cost',
} as const;
export type FeeBasis = (typeof FeeBasis)[keyof typeof FeeBasis];
export const FeeBasisToLabel: Record<FeeBasis, string> = {
  committed: 'Committed capital',
  'invested-cost': 'Invested cost',
};

export const ExpenseFrequency = {
  oneTime: 'one-time',
  annual: 'annual',
} as const;
export type ExpenseFrequency = (typeof ExpenseFrequency)[keyof typeof ExpenseFrequency];
export const ExpenseFrequencyToLabel: Record<ExpenseFrequency, string> = {
  'one-time': 'One-time',
  annual: 'Every quarter',
};

export const PacingMethod = {
  even: 'even',
  frontLoaded: 'front-loaded',
  custom: 'custom',
} as const;
export type PacingMethod = (typeof PacingMethod)[keyof typeof PacingMethod];

function selectField<T extends string>(labels: Record<T, string>) {
  return yup
    .string()
    .oneOf(Object.keys(labels) as T[])
    .formMeta({
      renderer: RendererType.singleSelect,
      rendererMeta: {
        values: (Object.entries(labels) as [T, string][]).map(([value, displayName]) => ({
          value,
          displayName,
        })),
      },
    });
}

export function managementFeePeriodFields() {
  return {
    id: yup.string().required().default(''),
    name: yup.string().required().label('Period').default(''),
    dateFrom: plainDateField().required().label('From').default(''),
    dateTo: plainDateField()
      .required()
      .label('To')
      .default('')
      .test('after-from', 'Must be after From', function (value) {
        return !value || !this.parent.dateFrom || value > this.parent.dateFrom;
      }),
    rate: percentField0to100().required().label('Rate').default(0),
    stepDown: yup
      .number()
      .min(0)
      .nullable()
      .label('Step-down (pts per year)')
      .formMeta({ renderer: RendererType.number })
      .default(null),
    basis: selectField(FeeBasisToLabel).required().label('Basis').default(FeeBasis.committed),
  };
}
export function managementFeePeriodSchema() {
  return yup.object(managementFeePeriodFields());
}
export type ManagementFeePeriod = yup.InferType<ReturnType<typeof managementFeePeriodSchema>>;

export function fundExpenseFields() {
  return {
    id: yup.string().required().default(''),
    name: yup.string().required().label('Expense').default(''),
    frequency: selectField(ExpenseFrequencyToLabel)
      .required()
      .label('Frequency')
      .default(ExpenseFrequency.oneTime),
    date: plainDateField()
      .nullable()
      .label('Date')
      .default(null)
      .when('frequency', {
        is: ExpenseFrequency.oneTime,
        then: (schema) => schema.required(),
      }),
    dateFrom: plainDateField().nullable().label('From (blank = inception)').default(null),
    dateTo: plainDateField().nullable().label('To (blank = term end)').default(null),
    amount: usdField().min(0).required().label('Amount (per year when recurring)').default(0),
  };
}
export function fundExpenseSchema() {
  return yup.object(fundExpenseFields());
}
export type FundExpense = yup.InferType<ReturnType<typeof fundExpenseSchema>>;

export function reserveStepFields() {
  return {
    id: yup.string().required().default(''),
    effectiveFrom: plainDateField().required().label('Effective From').default(''),
    percentage: percentField0to100().required().label('Reserves %').default(0),
  };
}
export function reserveStepSchema() {
  return yup.object(reserveStepFields());
}
export type ReserveStep = yup.InferType<ReturnType<typeof reserveStepSchema>>;

export function pacingFields() {
  return {
    method: yup
      .string()
      .oneOf(Object.values(PacingMethod))
      .required()
      .default<PacingMethod>(PacingMethod.even),
    customSplit: yup
      .array(yup.object({ year: yup.number().required(), percentage: yup.number().min(0).required() }))
      .nullable()
      .default(null),
  };
}
export function pacingSchema() {
  return yup.object(pacingFields());
}
export type Pacing = yup.InferType<ReturnType<typeof pacingSchema>>;

export function allocationSettingsFields() {
  return {
    allocationBasis: yup
      .string()
      .oneOf(Object.values(AllocationBasis))
      .nullable()
      .default<AllocationBasis | null>(null),
    managementFees: yup.array(managementFeePeriodSchema()).nullable().default(null),
    fundExpenses: yup.array(fundExpenseSchema()).nullable().default(null),
    reserveSteps: yup.array(reserveStepSchema()).nullable().default(null),
    pacing: yup
      .object({
        unassignedReserves: pacingSchema().nullable().default(null),
        unscheduledInitial: pacingSchema().nullable().default(null),
      })
      .nullable()
      .default(null),
  };
}
export function allocationSettingsSchema() {
  return yup.object(allocationSettingsFields());
}
export type AllocationSettings = yup.InferType<ReturnType<typeof allocationSettingsSchema>>;
